<template>
  <Teleport to="body">
    <Transition
      appear
      enter-active-class="tw:transition-opacity tw:duration-150 tw:ease-out"
      enter-from-class="tw:opacity-0"
      leave-active-class="tw:transition-opacity tw:duration-150 tw:ease-out"
      leave-to-class="tw:opacity-0"
    >
      <div
        v-if="open"
        :class="`${$options.name}__container`"
        class="tw:fixed tw:top-0 tw:left-0 tw:w-full tw:h-full tw:bg-black/60 tw:flex tw:items-center tw:justify-center tw:p-3"
        :data-e-ga-id="uniqueId"
        :style="`z-index: ${index}`"
        @click="onClickOutside"
      >
        <div
          :class="`${$options.name}`"
          class="tw:relative tw:bg-white tw:rounded-lg tw:border-none tw:p-6 tw:min-h-16 tw:max-h-full tw:flex tw:flex-col tw:gap-2 tw:overflow-auto"
          :style="`width: ${width / 16}rem; z-index: ${index};`"
          style="box-shadow: 4px 4px 24px rgba(0, 0, 0, 0.22)"
          role="dialog"
          v-bind="$attrs"
          @click="(e) => e.stopPropagation()"
        >
          <EdroneButtonIcon
            v-if="showCloseIcon"
            :class="`${$options.name}__close-icon`"
            class="tw:absolute tw:right-4 tw:top-4 tw:!text-default-600 tw:!text-xl"
            :disabled="false"
            :unique-id="uniqueId + '-modal-close-icon'"
            size="small"
            type="plain"
            @click="$emit('close')"
          >
            <font-awesome-icon icon="fa-light fa-xmark" />
          </EdroneButtonIcon>
          <p v-if="$slots.title" class="tw:w-[calc(100%_-_22px)] tw:heading-2">
            <!-- @slot To display the title of the modal. This slot is needed because it adds margin to the title to prevent collapsing it on the close icon-->
            <slot name="title"></slot>
          </p>
          <!-- @slot The content below the title -->
          <slot></slot>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script lang="ts">
import { defineComponent, onMounted, watch } from "vue";
import EdroneButtonIcon from "edrone-components/EdroneButtonIcon.vue";

export default defineComponent({
  name: "EdroneModal",
  components: {
    EdroneButtonIcon,
  },
  props: {
    /**
     * If `true`, the modal will be visible.
     */
    open: {
      type: Boolean,
      required: true,
    },
    /**
     * If `true`, the modal will close when the user clicks outside of it.
     */
    closeOnOutsideClick: {
      type: Boolean,
      required: false,
      default: true,
    },
    /**
     * If `true`, the modal will close when the user presses the `Esc` key.
     */
    closeOnEscKeyPress: {
      type: Boolean,
      required: false,
      default: true,
    },
    /**
     * If `true`, the modal will show the close icon.
     */
    showCloseIcon: {
      type: Boolean,
      required: false,
      default: true,
    },
    /**
     * Width of the modal in pixels.
     */
    width: {
      type: [Number],
      required: false,
      default: 400,
    },
    uniqueId: {
      type: String,
      required: false,
    },
    index: {
      type: Number,
      required: false,
      default: 41000,
    },
  },
  emits: {
    /**
     * Emitted when the user click outside the modal if `closeOnOutsideClick: true`,
     * when the user presses the `Esc` key if `closeOnEscKeyPress: true`,
     * or when the user clicks the close icon if `showCloseIcon: true`.
     */
    close: () => true,
  },
  setup(props, ctx) {
    onMounted(() => {
      window.addEventListener("keydown", onEscKeyPress);
    });

    watch(props, (newProps) => {
      if (newProps.open && props.closeOnEscKeyPress) {
        window.addEventListener("keydown", onEscKeyPress);
      } else {
        window.removeEventListener("keydown", onEscKeyPress);
      }
    });

    function onEscKeyPress(e: KeyboardEvent) {
      if (e.key === "Escape") {
        ctx.emit("close");
      }
    }

    return {
      onClickOutside: () => {
        if (props.closeOnOutsideClick && props.open) {
          ctx.emit("close");
        }
      },
    };
  },
});
</script>
